/**
 * Health check API utilities
 * Checks the health status of all services in the Lion Pro ecosystem
 */

import { proApiFetch } from './pro-api.client';

export interface HealthStatus {
  status: 'ok' | 'error';
  service: string;
  responseTime?: number;
  error?: string;
  timestamp?: string;
}

/**
 * Check pro-api health status
 */
export async function checkProApiHealth(): Promise<HealthStatus> {
  const startTime = Date.now();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';
  const fullUrl = `${apiUrl}/health`;
  
  try {
    const data = await proApiFetch<{ status: string; service: string; timestamp?: string }>('/health');
    const responseTime = Date.now() - startTime;
    
    return {
      status: 'ok',
      service: 'pro-api',
      responseTime,
      timestamp: data.timestamp || new Date().toISOString(),
    };
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    // Provide more detailed error messages
    let errorMessage = 'Failed to reach pro-api';
    if (error instanceof TypeError && error.message.includes('fetch')) {
      errorMessage = `Network error: Cannot connect to pro-api. Check if the service is running and CORS is configured. (URL: ${fullUrl})`;
    } else if (error.message) {
      errorMessage = `${error.message} (URL: ${fullUrl})`;
    } else {
      errorMessage = `Failed to reach pro-api (URL: ${fullUrl})`;
    }
    return {
      status: 'error',
      service: 'pro-api',
      responseTime,
      error: errorMessage,
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Check user-service health status via pro-api proxy
 */
export async function checkUserServiceHealth(): Promise<HealthStatus> {
  const startTime = Date.now();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';
  const fullUrl = `${apiUrl}/health/user-service`;
  
  try {
    const data = await proApiFetch<{ 
      status: string; 
      service: string; 
      responseTime?: number;
      timestamp?: string;
      error?: string;
    }>('/health/user-service');
    const responseTime = Date.now() - startTime;
    
    if (data.status === 'error' || data.error) {
      return {
        status: 'error',
        service: 'user-service',
        responseTime: data.responseTime || responseTime,
        error: data.error || 'User-service health check failed',
        timestamp: data.timestamp || new Date().toISOString(),
      };
    }
    
    return {
      status: 'ok',
      service: 'user-service',
      responseTime: data.responseTime || responseTime,
      timestamp: data.timestamp || new Date().toISOString(),
    };
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    // Provide more detailed error messages
    let errorMessage = 'Failed to reach user-service via pro-api';
    if (error instanceof TypeError && error.message.includes('fetch')) {
      errorMessage = `Network error: Cannot connect to pro-api (which proxies user-service). Check if pro-api is running and CORS is configured. (URL: ${fullUrl})`;
    } else if (error.message) {
      errorMessage = `${error.message} (URL: ${fullUrl})`;
    } else {
      errorMessage = `Failed to reach user-service via pro-api (URL: ${fullUrl})`;
    }
    return {
      status: 'error',
      service: 'user-service',
      responseTime,
      error: errorMessage,
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Check product-service health status via pro-api proxy
 */
export async function checkProductServiceHealth(): Promise<HealthStatus> {
  const startTime = Date.now();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';
  const fullUrl = `${apiUrl}/health/product-service`;

  try {
    const data = await proApiFetch<{
      status: string;
      service: string;
      responseTime?: number;
      timestamp?: string;
      error?: string;
    }>('/health/product-service');
    const responseTime = Date.now() - startTime;

    if (data.status === 'error' || data.error) {
      return {
        status: 'error',
        service: 'product-service',
        responseTime: data.responseTime || responseTime,
        error: data.error || 'Product-service health check failed',
        timestamp: data.timestamp || new Date().toISOString(),
      };
    }

    return {
      status: 'ok',
      service: 'product-service',
      responseTime: data.responseTime || responseTime,
      timestamp: data.timestamp || new Date().toISOString(),
    };
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    let errorMessage = 'Failed to reach product-service via pro-api';
    if (error instanceof TypeError && error.message.includes('fetch')) {
      errorMessage = `Network error: Cannot connect to pro-api (which proxies product-service). (URL: ${fullUrl})`;
    } else if (error.message) {
      errorMessage = `${error.message} (URL: ${fullUrl})`;
    }
    return {
      status: 'error',
      service: 'product-service',
      responseTime,
      error: errorMessage,
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Check analytics-service health status via pro-api proxy
 */
export async function checkAnalyticsServiceHealth(): Promise<HealthStatus> {
  const startTime = Date.now();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';
  const fullUrl = `${apiUrl}/health/analytics-service`;

  try {
    const data = await proApiFetch<{
      status: string;
      service: string;
      responseTime?: number;
      timestamp?: string;
      error?: string;
    }>('/health/analytics-service');
    const responseTime = Date.now() - startTime;

    if (data.status === 'error' || data.error) {
      return {
        status: 'error',
        service: 'analytics-service',
        responseTime: data.responseTime || responseTime,
        error: data.error || 'Analytics-service health check failed',
        timestamp: data.timestamp || new Date().toISOString(),
      };
    }

    return {
      status: 'ok',
      service: 'analytics-service',
      responseTime: data.responseTime || responseTime,
      timestamp: data.timestamp || new Date().toISOString(),
    };
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    let errorMessage = 'Failed to reach analytics-service via pro-api';
    if (error instanceof TypeError && error.message.includes('fetch')) {
      errorMessage = `Network error: Cannot connect to pro-api (which proxies analytics-service). (URL: ${fullUrl})`;
    } else if (error.message) {
      errorMessage = `${error.message} (URL: ${fullUrl})`;
    }
    return {
      status: 'error',
      service: 'analytics-service',
      responseTime,
      error: errorMessage,
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Check pro-web health status
 * Since pro-web is a static site, if this function is called, the site is healthy
 */
export async function checkProWebHealth(): Promise<HealthStatus> {
  // Pro-web is a static site, so if this code is running, it's healthy
  return {
    status: 'ok',
    service: 'pro-web',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Check all services health status
 */
export async function checkAllServicesHealth(): Promise<HealthStatus[]> {
  const [proApi, userService, productService, analyticsService, proWeb] = await Promise.allSettled([
    checkProApiHealth(),
    checkUserServiceHealth(),
    checkProductServiceHealth(),
    checkAnalyticsServiceHealth(),
    checkProWebHealth(),
  ]);

  return [
    proApi.status === 'fulfilled' ? proApi.value : {
      status: 'error' as const,
      service: 'pro-api',
      error: proApi.reason?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
    },
    userService.status === 'fulfilled' ? userService.value : {
      status: 'error' as const,
      service: 'user-service',
      error: userService.reason?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
    },
    productService.status === 'fulfilled' ? productService.value : {
      status: 'error' as const,
      service: 'product-service',
      error: productService.reason?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
    },
    analyticsService.status === 'fulfilled' ? analyticsService.value : {
      status: 'error' as const,
      service: 'analytics-service',
      error: analyticsService.reason?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
    },
    proWeb.status === 'fulfilled' ? proWeb.value : {
      status: 'error' as const,
      service: 'pro-web',
      error: proWeb.reason?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
    },
  ];
}
